var lyoraQuickAddState =
  globalThis.__lyoraQuickAddState ||
  (globalThis.__lyoraQuickAddState = {
    resolvedSectionId: globalThis.__lyoraQuickAddResolvedSectionId || '',
    productCache: new Map(),
    inFlightRequests: new Map(),
    maxCacheEntries: 12,
    cartUpdateSubscriptionAttached: false,
  });

if (
  !lyoraQuickAddState.cartUpdateSubscriptionAttached &&
  typeof subscribe === 'function' &&
  typeof PUB_SUB_EVENTS !== 'undefined'
) {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    lyoraQuickAddState.productCache.clear();
  });
  lyoraQuickAddState.cartUpdateSubscriptionAttached = true;
}

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        this.addEventListener('click', this.openMiniPdpMediaModal.bind(this));
        this.addEventListener('change', this.handleMiniPdpOptionChange.bind(this));
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.resetScrollPosition();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        if (opener.getAttribute('aria-disabled') === 'true') return;
        const loadingSpinner = opener.querySelector('.loading__spinner');
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        loadingSpinner?.classList.remove('hidden');

        return Promise.all([
          this.ensureProductStyles(),
          this.fetchProductContent(opener.getAttribute('data-product-url')),
        ])
          .then(([, productElement]) => {
            if (!productElement) throw new Error('Product content unavailable');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            this.stabilizeMiniPdpLayout();
          })
          .catch(() => {
            const error = document.createElement('div');
            error.className = 'quick-add-modal__error';
            const message = document.createElement('p');
            message.setAttribute('role', 'alert');
            message.textContent = window.variantStrings.quickViewError;
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.className = 'button button--primary';
            retry.textContent = window.variantStrings.retry;
            retry.addEventListener('click', () => {
              retry.disabled = true;
              this.show(opener);
            });
            error.append(message, retry);
            this.modalContent.replaceChildren(error);
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            loadingSpinner?.classList.add('hidden');
          });
      }

      fetchProductContent(productUrl) {
        const cachedProductElement = this.getCachedProductElement(productUrl);
        if (cachedProductElement) {
          return Promise.resolve(cachedProductElement);
        }

        const inFlightRequest = this.getInFlightRequest(productUrl);
        if (inFlightRequest) {
          return inFlightRequest.then((productHTML) => this.createProductElement(productHTML));
        }

        this.captureResolvedSectionIdFromPage();

        const requestPromise = this.requestProductHTML(productUrl).finally(() => {
          this.clearInFlightRequest(productUrl);
        });

        this.setInFlightRequest(productUrl, requestPromise);

        return requestPromise.then((productHTML) => this.createProductElement(productHTML));
      }

      ensureProductStyles() {
        const template = document.getElementById('QuickAddProductStyles');
        if (!template) return Promise.resolve();
        if (lyoraQuickAddState.stylesheetPromise) return lyoraQuickAddState.stylesheetPromise;

        // Insert at the original cascade position, and finish loading before opening.
        lyoraQuickAddState.stylesheetPromise = new Promise((resolve, reject) => {
          const stylesheet = document.createElement('link');
          stylesheet.rel = 'stylesheet';
          stylesheet.href = template.dataset.stylesheetUrl;
          stylesheet.addEventListener('load', () => {
            template.remove();
            resolve();
          }, { once: true });
          stylesheet.addEventListener('error', () => {
            stylesheet.remove();
            lyoraQuickAddState.stylesheetPromise = null;
            reject(new Error('Product stylesheet unavailable'));
          }, { once: true });
          template.before(stylesheet);
        });
        return lyoraQuickAddState.stylesheetPromise;
      }

      prefetchProductContent(productUrl) {
        if (!productUrl) return Promise.resolve();
        return Promise.all([this.ensureProductStyles(), this.fetchProductContent(productUrl)])
          .then(([, productElement]) => productElement)
          .catch(() => null);
      }

      requestProductHTML(productUrl) {
        const requestUrl = this.buildProductRequestUrl(productUrl);

        return this.fetchProductDocument(requestUrl)
          .catch(() => null)
          .then((responseHTML) => {
            const productElement = responseHTML && this.getProductElement(responseHTML);

            if (this.hasRenderableProductInfo(productElement)) {
              this.captureResolvedSectionId(productElement);
              this.cacheProductElement(productUrl, productElement);
              return productElement.outerHTML;
            }

            return this.fetchFullProductDocument(productUrl).then((fallbackProductElement) => fallbackProductElement?.outerHTML || null);
          });
      }

      fetchProductDocument(url) {
        return fetch(url).then((response) => {
          if (!response.ok) {
            throw new Error(`Quick add request failed: ${response.status}`);
          }

          return response.text().then((responseText) => new DOMParser().parseFromString(responseText, 'text/html'));
        });
      }

      fetchFullProductDocument(productUrl) {
        return this.fetchProductDocument(productUrl).then((responseHTML) => {
          const productElement = this.getProductElement(responseHTML);
          if (!this.hasRenderableProductInfo(productElement)) return null;

          this.captureResolvedSectionId(productElement);
          this.cacheProductElement(productUrl, productElement);

          return this.createProductElement(productElement.outerHTML);
        });
      }

      buildProductRequestUrl(productUrl) {
        const requestUrl = new URL(productUrl, window.location.origin);
        requestUrl.searchParams.set('section_id', this.getPreferredSectionId());
        return requestUrl.toString();
      }

      getProductElement(responseHTML) {
        return responseHTML.querySelector('product-info');
      }

      hasRenderableProductInfo(productElement) {
        if (!productElement) return false;

        return !!productElement.querySelector("product-form button[name='add'][type='submit'], variant-selects, .product__title");
      }

      getPreferredSectionId() {
        return lyoraQuickAddState.resolvedSectionId || this.dataset.quickViewSectionId || 'main-product';
      }

      captureResolvedSectionId(productElement) {
        const resolvedSectionId = productElement?.dataset?.section;
        if (resolvedSectionId) {
          lyoraQuickAddState.resolvedSectionId = resolvedSectionId;
          globalThis.__lyoraQuickAddResolvedSectionId = resolvedSectionId;
        }
      }

      captureResolvedSectionIdFromPage() {
        if (lyoraQuickAddState.resolvedSectionId) return;

        this.captureResolvedSectionId(document.querySelector("product-info[id^='MainProduct']"));
      }

      getCacheKey(productUrl) {
        const normalizedUrl = new URL(productUrl, window.location.origin);
        normalizedUrl.searchParams.delete('section_id');
        normalizedUrl.hash = '';
        return normalizedUrl.toString();
      }

      getInFlightRequest(productUrl) {
        return lyoraQuickAddState.inFlightRequests.get(this.getCacheKey(productUrl));
      }

      setInFlightRequest(productUrl, requestPromise) {
        lyoraQuickAddState.inFlightRequests.set(this.getCacheKey(productUrl), requestPromise);
      }

      clearInFlightRequest(productUrl) {
        lyoraQuickAddState.inFlightRequests.delete(this.getCacheKey(productUrl));
      }

      getCachedProductElement(productUrl) {
        const cachedProduct = lyoraQuickAddState.productCache.get(this.getCacheKey(productUrl));
        if (!cachedProduct?.productHTML) return null;

        if (cachedProduct.resolvedSectionId) {
          lyoraQuickAddState.resolvedSectionId = cachedProduct.resolvedSectionId;
          globalThis.__lyoraQuickAddResolvedSectionId = cachedProduct.resolvedSectionId;
        }

        return this.createProductElement(cachedProduct.productHTML);
      }

      cacheProductElement(productUrl, productElement) {
        if (!productElement?.outerHTML) return;

        const cacheKey = this.getCacheKey(productUrl);
        const resolvedSectionId = productElement.dataset?.section || '';

        if (lyoraQuickAddState.productCache.has(cacheKey)) {
          lyoraQuickAddState.productCache.delete(cacheKey);
        }

        lyoraQuickAddState.productCache.set(cacheKey, {
          productHTML: productElement.outerHTML,
          resolvedSectionId,
        });

        while (lyoraQuickAddState.productCache.size > lyoraQuickAddState.maxCacheEntries) {
          const oldestCacheKey = lyoraQuickAddState.productCache.keys().next().value;
          if (!oldestCacheKey) break;
          lyoraQuickAddState.productCache.delete(oldestCacheKey);
        }
      }

      createProductElement(productHTML) {
        if (!productHTML) return null;

        return new DOMParser().parseFromString(productHTML, 'text/html').querySelector('product-info');
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.prepareMiniPdp(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.syncMiniPdpPrimaryMedia(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal && this.dataset.quickViewMode !== 'mini-pdp') productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) {
          modalDialog.forEach((modal) => {
            if (modal.classList.contains('product-variant-size-guide-modal')) return;
            modal.remove();
          });
        }
      }

      prepareMiniPdp(productElement) {
        if (this.dataset.quickViewMode !== 'mini-pdp') return;

        productElement.classList.add('quick-add-product-info', 'quick-add-product-info--mini-pdp');
        const product = productElement.querySelector('.product');
        product?.classList.remove('grid', 'grid--2-col-tablet', 'product--thumbnail', 'product--thumbnail_slider');

        productElement.querySelectorAll('.product__column-sticky').forEach((column) => {
          column.classList.remove('product__column-sticky');
        });

        const infoContainer = productElement.querySelector('.product__info-container');
        if (!infoContainer) return;

        infoContainer
          .querySelectorAll(
            '.product__accordion, .product__description, .complementary-products, .installment, .product__tax, .product__xr-button, .icon-with-text, .lyora-icon-columns'
          )
          .forEach((element) => element.remove());

        infoContainer.querySelectorAll(':scope > .shopify-app-block').forEach((appBlock, index) => {
          if (index > 0) appBlock.remove();
        });

        const viewDetailsLink = infoContainer.querySelector('.product__view-details');
        if (viewDetailsLink) {
          viewDetailsLink.classList.add('product__view-details--mini-pdp');
        }

        this.repositionMiniPdpMedia(productElement, infoContainer);
      }

      repositionMiniPdpMedia(productElement, infoContainer) {
        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        if (!mediaWrapper) return;

        mediaWrapper.classList.add('quick-add-mini-pdp__gallery');

        const priceBlock = infoContainer.querySelector('[id^="price-"]');
        const insertAfterNode = priceBlock || infoContainer.querySelector('.product__title') || infoContainer.firstElementChild;

        if (insertAfterNode?.parentNode === infoContainer) {
          insertAfterNode.insertAdjacentElement('afterend', mediaWrapper);
        } else {
          infoContainer.prepend(mediaWrapper);
        }
      }

      resetScrollPosition() {
        this.scrollTop = 0;
        this.querySelector('.quick-add-modal__content')?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
        this.querySelector('.quick-add-modal__content-info')?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
        this.querySelector('[role="dialog"]')?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
      }

      stabilizeMiniPdpLayout() {
        this.resetScrollPosition();
        this.querySelector('[id^="ModalClose-"]')?.focus?.({ preventScroll: true });
        this.syncMiniPdpPrimaryMedia();

        requestAnimationFrame(() => {
          this.resetScrollPosition();
          this.querySelector('[id^="ModalClose-"]')?.focus?.({ preventScroll: true });
          this.syncMiniPdpPrimaryMedia();

          window.setTimeout(() => {
            this.resetScrollPosition();
            this.querySelector('[id^="ModalClose-"]')?.focus?.({ preventScroll: true });
            this.syncMiniPdpPrimaryMedia();
          }, 80);
        });
      }

      handleMiniPdpOptionChange(event) {
        if (this.dataset.quickViewMediaMode !== 'single') return;
        if (!event.target.closest('variant-selects, fieldset, .product-form__input')) return;

        window.setTimeout(() => {
          this.syncMiniPdpPrimaryMedia();
        }, 140);
      }

      syncMiniPdpPrimaryMedia(root = this.modalContent) {
        if (this.dataset.quickViewMediaMode !== 'single') return;

        const mediaList = root?.querySelector?.('media-gallery ul');
        if (!mediaList) return;

        const activeMediaItem =
          mediaList.querySelector('.product__media-item.is-active') || mediaList.querySelector('li[data-media-id]');

        if (activeMediaItem && mediaList.firstElementChild !== activeMediaItem) {
          mediaList.prepend(activeMediaItem);
        }

        mediaList.scrollLeft = 0;
      }

      openMiniPdpMediaModal(event) {
        if (this.dataset.quickViewMode !== 'mini-pdp') return;

        const mediaOpener = event.target.closest('.quick-add-mini-pdp__gallery .product__modal-opener');
        if (!mediaOpener) return;

        const trigger = mediaOpener.querySelector('.product__media-toggle');
        const modalSelector = mediaOpener.getAttribute('data-modal');
        const mediaModal = modalSelector ? this.querySelector(modalSelector) : null;

        if (!trigger || !mediaModal || typeof mediaModal.show !== 'function') return;

        event.preventDefault();
        event.stopPropagation();
        this.resetScrollPosition();
        mediaModal.show(trigger);
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
